/**
 * useVqeExecuteShortcut — scoped keybinding target that runs the statement
 * the Query tab's Visual view shows (Plan 6 §3.3 S8, decision 8).
 *
 * The central dispatcher owns capture, so the target still works when the
 * VQE root itself is not focusable and focus rests on document.body.
 *
 *  - It fires EVEN when focus is inside an input/textarea/Monaco — Mod+Enter
 *    is standard DB-tool behavior for "run the query" and, being a chorded
 *    key, never collides with the chip builders' plain `Enter` handling. So,
 *    unlike `useVqeUndoShortcut`'s text-entry guard, there is none here.
 *  - Dispatcher capture runs before descendant key handlers, so a chip
 *    builder cannot swallow Mod+Enter while committing its own plain Enter.
 *
 * Guards: only the active pane's hook may act (`isActivePane`), and the
 * shortcut is suppressed while the destructive-query confirmation dialog is
 * open for this tab (`dangerDialogOpen`) — Enter inside that dialog must not
 * also re-trigger Execute.
 */
import { useLayoutEffect, useRef } from 'react';

import { useWorkspaceStore } from '../../core/store/workspaceStore';
import {
  registerKeybindingTarget,
  type KeybindingHandlersRef,
} from '../../../keybindings/dispatch/targetRegistry';

/** `'run-on-change'` never reaches this dispatcher — it is `runVisualStatement`'s own trigger for the Visual view's auto-run, not a keybinding source. */
export type VqeExecuteSource = 'button' | 'shortcut' | 'run-on-change';

export interface UseVqeExecuteShortcutArgs {
  readonly isActivePane: boolean;
  readonly dangerDialogOpen: boolean;
  readonly execute: (source: VqeExecuteSource) => void;
  /**
   * The surface's root. When given, the shortcut acts only while focus is
   * inside it (or nowhere) — a Query tab's Visual view shares its tab with the
   * AI chat, whose own Cmd/Ctrl+Enter sends the prompt.
   */
  readonly getRoot?: () => HTMLElement | null;
}

export const VQE_KEYBINDING_COMMAND_IDS = ['vqe.execute'] as const;

export function useVqeExecuteShortcut(args: UseVqeExecuteShortcutArgs): void {
  const { isActivePane, dangerDialogOpen, execute, getRoot } = args;
  const handlersRef = useRef<KeybindingHandlersRef>({ current: {} });
  handlersRef.current.current = {
    'vqe.execute': () => execute('shortcut'),
  };
  const canExecuteRef = useRef<() => boolean>(() => false);
  canExecuteRef.current = () => {
    if (!isActivePane || dangerDialogOpen) return false;
    const root = getRoot?.();
    const focused = document.activeElement;
    // A picker or filter popover the cards opened is portalled to <body>.
    return (
      !root ||
      !focused ||
      focused === document.body ||
      root.contains(focused) ||
      focused.closest('[data-radix-popper-content-wrapper]') != null
    );
  };

  useLayoutEffect(() => {
    if (!isActivePane) return undefined;
    const tabId = useWorkspaceStore.getState().activeTabId;
    if (!tabId) return undefined;
    return registerKeybindingTarget({
      scope: 'vqe',
      tabId,
      handlers: handlersRef.current,
      canExecute: () => canExecuteRef.current(),
    });
  }, [isActivePane]);
}
